import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Video } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Family } from "protogen/advisors_service_pb";
import { useGetUpcomingEventCalls } from "services/calendar";
import { EventNotice } from "protogen/calendar_pb";
import NoticeBanner from "./NoticeBanner";

const formatEventTime = (startSec: number, displayTimezone?: string) => {
  const date = new Date(startSec * 1000);

  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  };

  if (displayTimezone) {
    options.timeZone = displayTimezone;
  }

  const fullTimeStr = date.toLocaleTimeString("en-US", options);

  return `Today, ${fullTimeStr}`;
};

interface UpcomingVideoCallsProps {
  family: Family;
  onEventsLoaded?: (count: number) => void;
}

export default ({ family, onEventsLoaded }: UpcomingVideoCallsProps) => {
  const [events, setEvents] = useState<EventNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const { request } = useGetUpcomingEventCalls((response) => {
    if (response) {
      const now = new Date();
      const endOfToday = new Date(now);
      endOfToday.setHours(23, 59, 59, 999);

      const upcomingEvents = response.eventNotices.filter((event) => {
        const eventTime = new Date(Number(event.endSec) * 1000);
        return eventTime >= now && eventTime <= endOfToday;
      });
      setEvents(upcomingEvents);
      onEventsLoaded?.(upcomingEvents.length);
    } else {
      onEventsLoaded?.(0);
    }
    setLoading(false);
  });

  useEffect(() => {
    if (!family?.ref) return;

    request({
      familyRefs: [family.ref],
    });
  }, [family?.ref]);

  if (loading || !events.length) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {events.map((event) => (
        <NoticeBanner
          key={event.eventRef}
          eyebrowText={formatEventTime(
            Number(event.startSec),
            event.displayTimezone,
          )}
          title={event.title}
          buttonText="Join call"
          buttonIcon={<Video size={18} />}
          onClick={() => {
            if (event.videoRoom?.uuid) {
              navigate(`/video/${event.videoRoom.uuid}`);
            }
          }}
        />
      ))}
    </Box>
  );
};
